import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, TextField, Typography } from '@mui/material';

import { CommunicationType } from '@bandido/shared/types';

import { AudioPlayer } from './AudioPlayer';
import { AudioRecorder } from './AudioRecorder';
import { FileUploader } from './FileUploader';

interface CommunicationSectionProps {
  communicationType?: CommunicationType;
  audioUrl?: string;
  fileUrl?: string;
  text?: string;
  isRecording: boolean;
  isPlaying: boolean;
  isUploading: boolean;
  sendToTeam: boolean;
  recordingTime: number;
  audioFile: File | null;
  currentPlayTime: number;
  audioRef: RefObject<HTMLAudioElement>;
  fileInputRef: RefObject<HTMLInputElement>;
  formatTime: (seconds: number) => string;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onDeleteRecording: () => void;
  onPlayAudio: () => void;
  onPauseAudio: () => void;
  onTextChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteFile: () => void;
}

export const CommunicationSection = ({
  communicationType,
  audioUrl,
  fileUrl,
  text,
  isRecording,
  recordingTime,
  audioFile,
  isPlaying,
  currentPlayTime,
  audioRef,
  fileInputRef,
  isUploading,
  formatTime,
  onStartRecording,
  onStopRecording,
  onDeleteRecording,
  onPlayAudio,
  onPauseAudio,
  onTextChange,
  onFileUpload,
  onDeleteFile,
  sendToTeam,
}: CommunicationSectionProps) => {
  const progress = audioRef.current?.duration ? Math.min((currentPlayTime / audioRef.current.duration) * 100, 100) : 0;

  const renderCommunicationComponent = () => {
    const { t } = useTranslation();
    if (communicationType === CommunicationType.TEXT_TO_SPEECH && !sendToTeam) {
      return (
        <Stack gap={1}>
          <Typography variant="subtitle2">{t('callsMenu.selectedStage.text.title')}</Typography>
          <TextField
            multiline
            rows={4}
            value={text}
            onChange={onTextChange}
            size="small"
            name="text"
            fullWidth
            placeholder={t('callsMenu.selectedStage.text.textPlaceholder')}
            variant="outlined"
          />
        </Stack>
      );
    }

    if (communicationType === CommunicationType.AUDIO_RECORD) {
      if (audioUrl && audioRef.current) {
        return (
          <Stack gap={2}>
            <AudioPlayer
              isPlaying={isPlaying}
              currentPlayTime={currentPlayTime}
              progress={progress}
              formatTime={formatTime}
              onPlay={onPlayAudio}
              onPause={onPauseAudio}
              onDelete={onDeleteRecording}
            />
          </Stack>
        );
      }

      return (
        <AudioRecorder
          isRecording={isRecording}
          recordingTime={recordingTime}
          formatTime={formatTime}
          onStartRecording={onStartRecording}
          onStopRecording={onStopRecording}
          onDeleteRecording={onDeleteRecording}
        />
      );
    }

    if (communicationType === CommunicationType.AUDIO_FILE) {
      if (fileUrl && audioRef.current) {
        return (
          <Stack gap={2}>
            <AudioPlayer
              isPlaying={isPlaying}
              currentPlayTime={currentPlayTime}
              progress={progress}
              formatTime={formatTime}
              onPlay={onPlayAudio}
              onPause={onPauseAudio}
              onDelete={onDeleteFile}
            />
          </Stack>
        );
      }

      return (
        <FileUploader
          isUploading={isUploading}
          audioFile={audioFile}
          onFileUpload={onFileUpload}
          fileInputRef={fileInputRef}
          onDeleteFile={onDeleteFile}
        />
      );
    }

    return null;
  };

  return <div className="nodrag">{renderCommunicationComponent()}</div>;
};
